<template>
    <b-row class="estimate-details">
        <b-col xl="4" md="4" sm="4">
            <b-table-simple borderless>
                <b-tbody>
                    <input
                        type="hidden"
                        name="estimate-id"
                        :value="estimate.id"
                    />
                    <b-tr class="row">
                        <b-th class="col-4">Title:</b-th>
                        <b-td class="col-8">
                            <b-input
                                name="estimate-title"
                                v-model="updatedEstimate.new_title"
                                v-validate="'required'"
                                placeholder="Estimate name (required)"
                                :class="{
                                    'border-danger': errors.has(
                                        'estimate-title'
                                    )
                                }"
                            />
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Client:</b-th>
                        <b-td class="col-8" v-if="availableClients.length > 0">
                            <select
                                id="status"
                                v-model="updatedEstimate.new_client.id"
                                class="form-control"
                            >
                                <option
                                    v-for="client in availableClients"
                                    :key="client.id"
                                    :value="client.id"
                                >
                                    {{ client.label }}
                                </option>
                            </select>
                        </b-td>
                        <b-td class="col-8" v-else>
                            <vet-select
                                id="client_id"
                                v-model="updatedEstimate.new_client"
                                :class="{
                                    'border-danger': errors.has('client_id')
                                }"
                                :show-menu="false"
                                module="client"
                                placeholder="Client name (optional)"
                                name="client_id"
                            />
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Created:</b-th>
                        <b-td class="col-8">
                            <date-picker
                                id="start"
                                :config="datePickerOptions"
                                v-model="updatedEstimate.new_start"
                                v-validate="'required'"
                                :class="{
                                    'border-danger': errors.has('start')
                                }"
                                class="form-control"
                                placeholder="Create Date (required)"
                                name="start"
                            ></date-picker>
                        </b-td>
                    </b-tr>
                    <b-tr class="row" v-if="!estimate.isconvert">
                        <b-th class="col-4">Valid Until:</b-th>
                        <b-td class="col-8">
                            <date-picker
                                id="expire"
                                :config="datePickerOptions"
                                v-model="updatedEstimate.new_expire"
                                v-validate="'required'"
                                :class="{
                                    'border-danger': errors.has('expire')
                                }"
                                class="form-control"
                                placeholder="Valid Date (required)"
                                name="expire"
                            ></date-picker>
                        </b-td>
                    </b-tr>
                    <b-tr class="row" v-else>
                        <b-th class="col-4">Reference:</b-th>
                        <b-td class="col-8" v-if="estimate.invoice">
                            <invoice-link
                                v-if="estimate.invoice.encoded_id"
                                :invoice="estimate.invoice"
                                :newTab="false"
                            />
                        </b-td>
                        <b-td class="col-8" v-else>
                            {{ estimate.invoice }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Status:</b-th>
                        <b-td class="col-8">
                            {{ estimate.status_label }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
        <b-col xl="4" md="4" sm="4">
            <b-table-simple borderless>
                <b-tbody>
                    <b-tr class="row">
                        <b-th class="col-4">Location:</b-th>
                        <b-td class="col-8" v-if="checkLocationService()">
                            <template>
                                <select
                                    id="location"
                                    v-model="updatedEstimate.new_location"
                                    class="form-control"
                                >
                                    <option
                                        :value="inClinic"
                                        v-if="$root.setting.location == 2"
                                    >
                                        In Clinic
                                    </option>
                                    <option
                                        v-for="location in locations"
                                        :key="location.id"
                                        :value="location"
                                    >
                                        {{ location.name }}
                                        {{ location.address }}
                                    </option>
                                </select>
                            </template>
                        </b-td>
                        <b-td class="col-8" v-else>
                            {{ estimate.ismobile ? "At Client" : "In Clinic" }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Notes: </b-th>
                        <b-td class="col-8">
                            <editable
                                name="note"
                                v-model="updatedEstimate.new_note"
                                placeholder="Note (optional)"
                                :defaultHeight="64"
                            ></editable>
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Medical Note:</b-th>
                        <b-td class="col-8">
                            <v-select
                                id="medical-notes"
                                :options="medicalNotesFiltered"
                                v-model="new_soap"
                                placeholder="Medical Note (optional)"
                                label="label_in_estimate"
                                name="medical-notes"
                                @input="onNotesChange"
                                :class="{ 'border-danger': notesHasError }"
                                class="v-select-nowrap show-actions"
                                multiple
                                :loading="loading.medicalNote"
                            >
                            </v-select>
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Signature:</b-th>
                        <b-td class="col-8" v-if="estimate.signature_img">
                            <span v-html="estimate.signature_img"></span>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
        <b-col xl="4" md="4" sm="4">
            <b-table-simple borderless>
                <b-tbody>
                    <b-tr class="row">
                        <b-th class="col-4">Subtotal:</b-th>
                        <b-td class="col-8">
                            {{ estimate.sub_total_amount }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Discount:</b-th>
                        <b-td class="col-8">
                            {{ estimate.discount_amount }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Tax:</b-th>
                        <b-td class="col-8">
                            {{ estimate.tax_amount }}
                        </b-td>
                    </b-tr>
                    <b-tr class="row">
                        <b-th class="col-4">Total:</b-th>
                        <b-td class="col-8">
                            {{ estimate.total_amount }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-col>
    </b-row>
</template>
<script>
/**
 * @name EstimateDisplay
 * @see apps/application/views/staff/billing/invoice/estimate/create_update.php
 */

import InvoiceLink from "@/components/commons/invoice-link";
import BILLINGS from "@/constants/billings";
import {
    includes,
    forEach,
    indexOf,
    differenceBy,
    keyBy,
    filter
} from "lodash";
import vetSelect from "@/module/commons/vet-select.vue";
import Client from "@/services/client";
import vSelect from "vue-select";
import Billings from "@/services/client-billings";
import InvoiceGeneralService from "@/services/invoice/invoice-general.js";
import Editable from "@/components/elements/editable-textarea";

export default {
    mixins: [],
    components: {
        InvoiceLink,
        vetSelect,
        vSelect,
        Editable
    },
    props: {
        estimate: {
            type: Object
        }
    },
    data() {
        return {
            BILLINGS,
            updatedEstimate: {
                new_title: null,
                new_client: null,
                new_note: null,
                new_start: null,
                new_expire: null,
                new_location: null,
                start: null,
                id: null,
                client: null,
                status: null,
                location: null,
                active_soap: null,
                default_soap_ids: ""
            },
            new_soap: [],
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            locations: [],
            inClinic: {
                id: 1,
                name: "In Clinic",
                address: null
            },
            serviceLocation: {
                mobile: 1,
                mobileAndOnPremise: 2
            },
            medicalNotes: [],
            notesHasError: false,
            loading: {
                medicalNote: false
            }
        };
    },
    beforeMount() {
        this.datePickerOptions.format = this.getDateFormat();

        this.updatedEstimate.id = this.estimate.id;
        this.updatedEstimate.new_title = this.estimate.title;
        this.updatedEstimate.new_client = this.estimate.client;
        this.updatedEstimate.new_note = this.estimate.note;
        this.updatedEstimate.status = this.estimate.status;
        this.updatedEstimate.new_expire = this._carbonDateDisplay(
            this.estimate.expire
        );

        const startDatetime = this._fromDateCarbon(this.estimate.start);
        this.updatedEstimate.new_start = startDatetime.toFormat(
            this._dateFormat
        );
        // CANNOT CHANGE THE START TIME, HENCE WILL SET THE TIME SAME AS BEFORE
        this.updatedEstimate.start_time = startDatetime.toFormat("h:mm a");

        if (this.estimate.client) {
            this.getLocations(this.estimate.client.id);
        } else {
            // if no client, need to set the default location option
            this.setSelectedLocation();
        }

        this.getNoteOptions(this.estimate.id);
    },
    mounted() {},
    computed: {
        availableClients() {
            return this.$store.getters["billing/availableClients"];
        },
        medicalNotesFiltered() {
            return differenceBy(this.medicalNotes, this.new_soap, "id");
        }
    },
    watch: {
        "updatedEstimate.new_client": {
            async handler(newData, oldData) {
                let self = this;
                if (oldData === undefined) return;

                this.$store.commit("billing/setDirty", true);

                // update location list
                if (newData) {
                    this.getLocations(newData.id);
                } else if (newData === null) {
                    self.locations = [];
                    self.updatedEstimate.new_location = this.inClinic;
                }
            }
        }
    },
    methods: {
        getSoapViewLink(soap) {
            return process.env.APPS + soap.ci_url;
        },
        async getLocations(clientId) {
            await Client.getLocations(clientId).then(response => {
                this.locations = response.resources;
                this.setSelectedLocation();
            });
        },
        setSelectedLocation() {
            let location = this.estimate.location_id;
            if (location) {
                this.locations.forEach(element => {
                    if (location == element.id) {
                        this.updatedEstimate.new_location = element;
                    }
                });
            } else {
                if (
                    this.$root.setting &&
                    this.serviceLocation.mobile == this.$root.setting.location
                ) {
                    // if mobile only
                    this.updatedEstimate.new_location = this.locations[0];
                } else {
                    this.updatedEstimate.new_location = this.inClinic;
                }
            }
        },
        checkLocationService() {
            // if setting is set to on premise, location will be hidden
            return (
                this.$root.setting &&
                includes(
                    [
                        this.serviceLocation.mobile,
                        this.serviceLocation.mobileAndOnPremise
                    ],
                    this.$root.setting.location
                )
            );
        },
        setNoteOptions() {
            let self = this;
            self.new_soap = [];
            self.updatedEstimate.default_soap_ids = "";
            const activeSoapIds = keyBy(self.estimate.active_soap, "id");
            self.new_soap = filter(
                self.medicalNotes,
                note => activeSoapIds[note.id]
            );

            self.updatedEstimate.default_soap_ids = self.new_soap
                .map(soap => {
                    return `${soap.id}#$${soap.patient_id}`;
                })
                .join(",");
        },
        getNoteOptions() {
            let self = this;
            self.medicalNotes = [];
            self.loading.medicalNote = true;
            Billings.getEstimateSoap(self.estimate.id)
                .then(response => {
                    self.medicalNotes = response.resource;
                })
                .then(() => {
                    self.setNoteOptions();
                })
                .finally(() => {
                    self.loading.medicalNote = false;
                });
        },
        onNotesChange(notes) {
            let self = this;
            let patients = [];
            self.notesHasError = false;

            if (notes.length > 1) {
                forEach(notes, function (note) {
                    if (indexOf(patients, note.patient_id) == -1) {
                        patients.push(note.patient_id);
                    } else {
                        self.notesHasError = true;
                    }
                });
            }
        },
        /////////////////
        // FLOW SAVING //
        /////////////////
        /**
         * @method isValid
         * @description To check is form dont have any error
         * @return  {Promise}
         */
        async checkFormIsValid() {
            let valid = await this.$validator.validateAll();
            if (!valid || this.notesHasError) {
                return false;
            }

            return true;
        },
        /**
         * @method proceedSave
         * @description
         */
        async proceedSave() {
            let currlocation =
                this.updatedEstimate.new_location ?? this.inClinic;
            let location = currlocation.id;

            if ("type" in currlocation) {
                location = `[${currlocation.type}]${location}`;
            }

            const newClientId = this.updatedEstimate.new_client
                ? this.updatedEstimate.new_client.id
                : null;

            let details = {
                edit: true,
                title: this.updatedEstimate.new_title,
                id: this.updatedEstimate.id,
                cid: newClientId,
                default_cid: this.updatedEstimate.new_client
                    ? this.updatedEstimate.new_client.id
                    : null,
                start_time: this.updatedEstimate.start_time,
                estimate_edit: true,
                estimate_decline: false,
                status: this.updatedEstimate.status,
                default_soap_ids: this.updatedEstimate.default_soap_ids,
                valid: 1,
                business_location: this.$root.setting.location,
                client_dropdown: newClientId,
                start: this.updatedEstimate.new_start,
                expire: this.updatedEstimate.new_expire,
                location_id: location,
                default_location: this.location
                    ? this.updatedEstimate.location.id
                    : null,
                note: this.updatedEstimate.new_note
                    ? this.updatedEstimate.new_note
                    : "",
                flag: 0,
                late_fees_credit: undefined
            };

            var formData = new FormData();

            for (var key in details) {
                formData.append(key, details[key]);
            }

            if (this.new_soap.length > 0) {
                forEach(this.new_soap, function (soap) {
                    formData.append(
                        "soap_ids[]",
                        soap.id + "#$" + soap.patient_id
                    );
                });
            }

            this.save(formData);
        },
        save(formData) {
            let generalErrorMsg = "Error: Unable to save";

            InvoiceGeneralService.updateInvoice(formData)
                .then(res => {
                    if (res) {
                        if (
                            typeof res == "string" &&
                            res.toLowerCase().includes("fatal error")
                        ) {
                            // fatal error like "Fatal error: Uncaught PDOException"
                            // in ci, cannot save when set the client to null from has value
                            // SQLSTATE[23000]: Integrity constraint violation: 1048 Column 'client_id' cannot be null
                            throw new Error(generalErrorMsg);
                        }

                        this._message("Estimate saved");
                        this.$emit("saved", {
                            success: true
                        });
                        this.$store.commit("billing/setDirty", false);
                    } else {
                        throw new Error(generalErrorMsg);
                    }
                })
                .catch(e => {
                    this._message(generalErrorMsg, "danger");

                    this.$emit("saved", {
                        success: false
                    });
                });
        }
    }
};
</script>
<style type="text/css" lang="scss" scoped>
.estimate-details th,
.estimate-details td {
    padding: 0px !important;
    line-height: 35px !important;
}
.estimate-details tr {
    padding-left: 1.5rem !important;
}

.fee-line {
    line-height: normal;
    padding-top: 7px;
}
</style>
